import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import BackofficeLayout from "@/components/BackofficeLayout";
import SettingsLayout from "@/components/SettingsLayout";
import LoginPage from "@/pages/LoginPage";
import SignupPage from "@/pages/SignupPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import AcceptInvitationPage from "@/pages/AcceptInvitationPage";
import DashboardPage from "@/pages/DashboardPage";
import PocPage from "@/pages/PocPage";
import ProjectDetailPage from "@/pages/ProjectDetailPage";
import PipelinePage from "@/pages/PipelinePage";
import ContactsPage from "@/pages/ContactsPage";
import ContactDetailPage from "@/pages/ContactDetailPage";
import ChantierPage from "@/pages/ChantierPage";
import ChantiersListPage from "@/pages/ChantiersListPage";
import TasksListPage from "@/pages/TasksListPage";
import QuotesListPage from "@/pages/QuotesListPage";
import QuoteEditorPage from "@/pages/QuoteEditorPage";
import InvoicesListPage from "@/pages/InvoicesListPage";
import PublicQuotePage from "@/pages/PublicQuotePage";
import HubPage from "@/pages/HubPage";
import SupplierInvoicesPage from "@/pages/SupplierInvoicesPage";
import LeadSourcesPage from "@/pages/settings/LeadSourcesPage";
import IngestLogPage from "@/pages/settings/IngestLogPage";
import CompanySettingsPage from "@/pages/settings/CompanySettingsPage";
import PerformancePage from "@/pages/PerformancePage";
import InvoicingSettingsPage from "@/pages/settings/InvoicingSettingsPage";
import PeppolSettingsPage from "@/pages/settings/PeppolSettingsPage";
import CallIdentificationPage from "@/pages/settings/CallIdentificationPage";
import PeppolTenantsAdminPage from "@/pages/PeppolTenantsAdminPage";
import AdminAuditVatPage from "@/pages/AdminAuditVatPage";
import AdminDedupPage from "@/pages/AdminDedupPage";
import AdminEnvCheckPage from "@/pages/AdminEnvCheckPage";
import AdminPdfCachePage from "@/pages/AdminPdfCachePage";
import AdminPdfConservationPage from "@/pages/AdminPdfConservationPage";
import TeamPage from "@/pages/settings/TeamPage";
import SettingsIntegrationsPage from "@/pages/settings/SettingsIntegrationsPage";
import CatalogPage from "@/pages/settings/CatalogPage";
import ChantierTypesPage from "@/pages/settings/ChantierTypesPage";
import EmailSettingsPage from "@/pages/settings/EmailSettingsPage";
import EmailLogPage from "@/pages/settings/EmailLogPage";
import ReportsPage from "@/pages/ReportsPage";
import AccountPage from "@/pages/AccountPage";
import { MentionsPage, PrivacyPage, TermsPage } from "@/pages/legal/LegalPages";
// BLOC 19/10 — Fermeture signup public (P0 LLDLS)
import AccessRequestPage from "@/pages/AccessRequestPage";
import SignupInvitesAdminPage from "@/pages/super-admin/SignupInvitesAdminPage";
import AccessRequestsAdminPage from "@/pages/super-admin/AccessRequestsAdminPage";
import CompaniesAuditPage from "@/pages/super-admin/CompaniesAuditPage";
import { Toaster } from "@/components/ui/sonner";

function RootRedirect() {
  const { state } = useAuth();
  if (state === null) return null;
  return <Navigate to={state ? "/dashboard" : "/login"} replace />;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            <Route path="/" element={<RootRedirect />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/signup" element={<SignupPage />} />
            <Route path="/access-request" element={<AccessRequestPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/accept-invitation" element={<AcceptInvitationPage />} />
            <Route path="/legal/mentions" element={<MentionsPage />} />
            <Route path="/legal/privacy" element={<PrivacyPage />} />
            <Route path="/legal/terms" element={<TermsPage />} />
            <Route path="/:slug/c/quote/:token" element={<PublicQuotePage />} />
            <Route path="/lead/:hub_token/hub" element={<HubPage />} />

            <Route
              element={
                <ProtectedRoute>
                  <BackofficeLayout />
                </ProtectedRoute>
              }
            >
              <Route path="/dashboard" element={<DashboardPage />} />
              <Route path="/contacts" element={<ContactsPage />} />
              <Route path="/contacts/:contactId" element={<ContactDetailPage />} />
              <Route path="/leads" element={<Navigate to="/pipeline" replace />} />
              <Route
                path="/pipeline"
                element={
                  <ProtectedRoute hiddenForRoles={["gestionnaire_chantier"]}>
                    <PipelinePage />
                  </ProtectedRoute>
                }
              />
              <Route path="/leads/:leadId" element={<ProjectDetailPage />} />
              <Route path="/projects/:projectId" element={<ProjectDetailPage />} />
              <Route path="/chantiers" element={<ChantiersListPage />} />
              <Route path="/chantiers/:chantierId" element={<ChantierPage />} />
              <Route path="/tasks" element={<TasksListPage />} />
              <Route path="/quotes" element={<QuotesListPage />} />
              <Route path="/quotes/:quoteId" element={<QuoteEditorPage />} />
              <Route path="/factures" element={<InvoicesListPage />} />
              <Route path="/supplier-invoices" element={<SupplierInvoicesPage />} />
              <Route path="/poc" element={<PocPage />} />
              <Route path="/mon-compte" element={<AccountPage />} />
              <Route path="/rapports-comptables" element={<ReportsPage />} />
              <Route path="/performance" element={<PerformancePage />} />
              {/* Phase B lot correctifs (2026-09-10) — Catalogue accessible en
                  top-level (backend autorise gestionnaire_chantier sur POST/PATCH).
                  L'ancienne route /settings/catalog reste également disponible
                  pour l'admin (elle sert de shortcut depuis Paramètres). */}
              <Route path="/catalog" element={<CatalogPage />} />
              <Route
                path="/settings"
                element={
                  <ProtectedRoute adminOnly>
                    <SettingsLayout />
                  </ProtectedRoute>
                }
              >
                <Route index element={<Navigate to="/settings/company" replace />} />
                <Route path="company" element={<CompanySettingsPage />} />
                <Route path="integrations" element={<SettingsIntegrationsPage />} />
                <Route path="team" element={<TeamPage />} />
                <Route path="lead-sources" element={<LeadSourcesPage />} />
                <Route path="webhooks-log" element={<IngestLogPage />} />
                <Route path="catalog" element={<CatalogPage />} />
                <Route path="chantier-types" element={<ChantierTypesPage />} />
                <Route path="email" element={<EmailSettingsPage />} />
                <Route path="invoicing" element={<InvoicingSettingsPage />} />
                <Route path="peppol" element={<PeppolSettingsPage />} />
                <Route path="call-identification" element={<CallIdentificationPage />} />
                <Route path="email-log" element={<EmailLogPage />} />
              </Route>
              <Route path="/admin/peppol/tenants" element={<PeppolTenantsAdminPage />} />
              {/* BLOC 19/10 — Super-admin signup closure (P0 LLDLS) */}
              <Route path="/admin/signup-invites" element={<SignupInvitesAdminPage />} />
              <Route path="/admin/signup-requests" element={<AccessRequestsAdminPage />} />
              <Route path="/admin/companies-audit" element={<CompaniesAuditPage />} />
              <Route
                path="/admin/audit"
                element={
                  <ProtectedRoute adminOnly>
                    <AdminAuditVatPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/dedup"
                element={
                  <ProtectedRoute adminOnly>
                    <AdminDedupPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/env-check"
                element={
                  <ProtectedRoute adminOnly>
                    <AdminEnvCheckPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/pdf-cache"
                element={
                  <ProtectedRoute adminOnly>
                    <AdminPdfCachePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/pdf-conservation"
                element={
                  <ProtectedRoute adminOnly>
                    <AdminPdfConservationPage />
                  </ProtectedRoute>
                }
              />
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
      <Toaster theme="dark" richColors position="top-center" />
    </div>
  );
}

export default App;
